import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingFacade } from '../../../../../Facades/onboarding.facade';
import { CheckoutPreview } from '../../../../../Interfaces/checkout-preview.interface';
import { MINIMUM_DEVICE_LICENCES, MINIMUM_USER_LICENCES } from '../../../Consts/licences.const';
import { PageState } from '../../../Types/page-state.type';

@Component({
  selector: 'app-licences',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './licences.component.html',
  styleUrl: './licences.component.scss',
})
export class LicencesComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly onboardingFacade = inject(OnboardingFacade);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(FormBuilder);

  readonly minUserLicences = MINIMUM_USER_LICENCES;
  readonly minDeviceLicences = MINIMUM_DEVICE_LICENCES;

  form!: FormGroup;
  pageState = signal<PageState>('verifying');
  preview = signal<CheckoutPreview | null>(null);
  isSubmitting = signal(false);
  errorMessage = signal<string | null>(null);

  private readonly userLicences = signal(MINIMUM_USER_LICENCES);
  private readonly deviceLicences = signal(MINIMUM_DEVICE_LICENCES);

  readonly agentAvailable = computed(() => this.preview()?.agent != null);

  readonly monthlyTotal = computed(() => {
    const preview = this.preview();

    if (!preview) return 0;

    const agentAmount = preview.agent?.unit_amount ?? 0;

    return preview.seat.unit_amount * this.userLicences() + agentAmount * this.deviceLicences();
  });

  constructor() {
    this.buildForm();
    this.mirrorFormValues();
  }

  ngOnInit(): void {
    this.resolveEntryState();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      user_licences: [
        MINIMUM_USER_LICENCES,
        [Validators.required, Validators.min(MINIMUM_USER_LICENCES)],
      ],
      device_licences: [
        MINIMUM_DEVICE_LICENCES,
        [Validators.required, Validators.min(MINIMUM_DEVICE_LICENCES)],
      ],
    });
  }

  private mirrorFormValues(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.applyFormValues());
  }

  private applyFormValues(): void {
    this.userLicences.set(this.readCount('user_licences', MINIMUM_USER_LICENCES));
    this.deviceLicences.set(this.readCount('device_licences', MINIMUM_DEVICE_LICENCES));
  }

  private readCount(controlName: string, minimum: number): number {
    const raw = Number(this.form.get(controlName)?.value);

    return Number.isFinite(raw) && raw >= minimum ? Math.floor(raw) : minimum;
  }

  private resolveEntryState(): void {
    this.onboardingFacade.get({ skipLoading: true }).subscribe({
      next: (onboarding) => {
        if (!onboarding?.plan_data) {
          this.router.navigate(['/setup/plan-selection']);
          return;
        }

        this.loadPreview();
      },
      error: () => {
        this.router.navigate(['/setup/plan-selection']);
      },
    });
  }

  private loadPreview(): void {
    this.pageState.set('verifying');
    this.errorMessage.set(null);

    this.onboardingFacade.getCheckoutPreview({ skipLoading: true }).subscribe({
      next: (preview) => {
        this.preview.set(preview);
        this.applyPreviewToForm(preview);
        this.pageState.set('ready');
      },
      error: () => {
        this.errorMessage.set(this.translate.instant('onboarding.setup.review.errors.loadFailed'));
        this.pageState.set('error');
      },
    });
  }

  private applyPreviewToForm(preview: CheckoutPreview): void {
    const deviceControl = this.form.get('device_licences') as AbstractControl;

    this.form.patchValue({
      user_licences: Math.max(MINIMUM_USER_LICENCES, preview.seat.quantity),
      device_licences: preview.agent?.quantity ?? MINIMUM_DEVICE_LICENCES,
    });

    if (preview.agent) {
      deviceControl.enable();
      return;
    }

    deviceControl.disable();
  }

  retry(): void {
    this.loadPreview();
  }

  formatAmount(unitAmount: number): string {
    return Number.isInteger(unitAmount) ? String(unitAmount) : unitAmount.toFixed(2);
  }

  getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = { eur: '€', usd: '$', gbp: '£' };

    return symbols[currency.toLowerCase()] ?? currency.toUpperCase();
  }

  money(amount: number, currency: string): string {
    return this.getCurrencySymbol(currency) + this.formatAmount(amount);
  }

  onBack(): void {
    this.router.navigate(['/setup/plan-selection']);
  }

  onSubmit(): void {
    if (this.form.invalid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    const deviceLicences = this.agentAvailable() ? this.deviceLicences() : MINIMUM_DEVICE_LICENCES;

    this.onboardingFacade
      .saveLicences(this.userLicences(), deviceLicences)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.router.navigate(['/setup/connect-intune']);
        },
        error: (error: Error) => {
          this.errorMessage.set(
            error.message || this.translate.instant('onboarding.setup.licences.errors.saveFailed')
          );
        },
      });
  }
}
